import { NextApiResponse } from 'next';
import { Cacheability } from '../../constants/caching';
import {
  ICacheConfig,
  IClientCacheConfig,
  IPackagedCacheConfig,
  IPageConfig,
  IProjectConfig
} from '../../models/metaTypes';
import getNextConfig from './../../constants/getNextConfig';

const { appEnv, appName } = getNextConfig().publicRuntimeConfig;
const isNonCachedEnvironment = 'production,staging'.indexOf(appEnv) === -1;

// creating stash

// Browser caching documentation
// https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Cache-Control
// There is also fastly caching documentation that can be looked up

const CACHE_HEADER_KEY = 'Cache-Control';
const SURROGATE_CONTROL_HEADER_KEY = 'Surrogate-Control';
const DEBUG_HEADER_KEY = 'X-MP-Caching-Rules';
const SURROGATE_KEYS_HEADER_KEY = 'Surrogate-Key';

const getNoStorePackagedCacheConfig = (): IPackagedCacheConfig => ({
  key: 'No Store',
  edgeConfig: { maxAge: 0 },
  clientConfig: { cachability: Cacheability.NoStore }
});

const getConfigDebugString = (
  config?: ICacheConfig | IClientCacheConfig
): string => {
  if (config) {
    if (config.cachability === Cacheability.NoStore) {
      return Cacheability.NoStore;
    }

    if (Number.isSafeInteger(config.maxAge)) {
      // get a pretty string with the number of minutes/seconds that this is ttl'd for
      let timeToLive = '0';
      let expiresString = '';

      if ((config.maxAge ?? 0) > 0) {
        timeToLive = new Date(1000 * (config.maxAge ?? 0))
          .toISOString()
          .substring(11, 19);

        const expires = new Date();
        expires.setSeconds(expires.getSeconds() + (config.maxAge ?? 0));
        expiresString = ` (Expires: ${expires.toISOString()})`;
      }

      return `${timeToLive}${expiresString}`;
    }
  }

  return 'No config';
};

const getPackagedConfigDebugString = (
  packagedCacheConfig: IPackagedCacheConfig
): string =>
  `key: ${packagedCacheConfig.key} - edge: ${getConfigDebugString(
    packagedCacheConfig.edgeConfig
  )}; client: ${getConfigDebugString(
    packagedCacheConfig.clientConfig
  )}; created: ${new Date().toISOString()}`;

const getHeaderValue = (config: ICacheConfig | IClientCacheConfig): string => {
  const value: string[] = [];

  if (config.cachability) {
    value.push(config.cachability ?? '');
  }

  if (config.revalidation) {
    value.push(config.revalidation);
  }

  if (Number.isSafeInteger(config.maxAge)) {
    value.push(`max-age=${config.maxAge}`);
  }

  return value.join(', ');
};

const getNaturalPageCacheRules = (
  projectConfig: IProjectConfig,
  pageConfig: IPageConfig
): IPackagedCacheConfig => {
  // Use page path as key
  const hierarchy =
    pageConfig?.pageMetaData?.hierarchy ||
    projectConfig?.projectMetaData?.hierarchy ||
    [];

  const pageKey: IPackagedCacheConfig = {
    key: hierarchy.join('/')
  };

  // Merge cache config
  return {
    ...pageKey,
    ...(projectConfig?.packagedCacheConfig ?? {}),
    ...(pageConfig?.packagedCacheConfig ?? {})
  };
};

// if the gssp data contains any of the 3 following, NEVER try to set the cache headers
// There is still a way for 404's, and rediercts to get caching headers - if the page returns a
// gsspError with a status of ~300,400 then it'd try to set the header still
// that is application managed so it's fine - notFound and redirect are nextjs managed so we want to leave it alone.
const addCachingHeaders = (
  cachingStatusCode: number,
  res: NextApiResponse,
  projectConfig: IProjectConfig,
  pageConfig: IPageConfig,
  isAdmin: boolean = false
) => {
  if (!res) return;

  const packagedConfig: IPackagedCacheConfig = getNoStorePackagedCacheConfig();
  const naturalCacheRules = getNaturalPageCacheRules(projectConfig, pageConfig);

  // we cannot cache pages that are not intended to be cached
  // this rule probably could be more complex but we will start here to deal with the photography checkout issues
  if (naturalCacheRules?.edgeConfig?.maxAge === 0) {
    Object.assign(packagedConfig, naturalCacheRules);
  } else if (isAdmin) {
    packagedConfig.key = 'admin';
  } else if (isNonCachedEnvironment) {
    packagedConfig.key = `Non-cached Enviroment (${appEnv})`;
  }
  // anything that's a 500 should only cache for 1min (60s)
  else if (cachingStatusCode >= 500) {
    packagedConfig.edgeConfig = { maxAge: 60 };
    packagedConfig.key = '500 Range Response';
  }

  // anything that's a 400 should only cache for 1h (3600s)
  else if (cachingStatusCode >= 400) {
    packagedConfig.edgeConfig = { maxAge: 3600 };
    packagedConfig.key = '400 Range Response';
  }

  // let the project or page config control the cache response
  else if (cachingStatusCode === 307 || cachingStatusCode === 302) {
    packagedConfig.key = 'Temporary Redirect';
    // use default - which is 0 cache
  } else if (cachingStatusCode >= 300) {
    packagedConfig.key = 'Permanent Redirect';
    packagedConfig.clientConfig = { maxAge: 600 };
    packagedConfig.edgeConfig = { maxAge: 600 };
  } else {
    // Merge cache config
    Object.assign(packagedConfig, naturalCacheRules);
  }

  // console.log('CACHE:', debugString);
  if (packagedConfig.clientConfig) {
    res.setHeader(
      CACHE_HEADER_KEY,
      getHeaderValue(packagedConfig.clientConfig)
    );
  }

  if (packagedConfig.edgeConfig) {
    res.setHeader(
      SURROGATE_KEYS_HEADER_KEY,
      `nextjs nextjs-${appName.toLowerCase()}`
    );

    res.setHeader(
      SURROGATE_CONTROL_HEADER_KEY,
      getHeaderValue(packagedConfig.edgeConfig)
    );
  }

  res.setHeader(DEBUG_HEADER_KEY, getPackagedConfigDebugString(packagedConfig));
};

export default addCachingHeaders;
